import { sortOnboardingStages } from '~/lib/onboardingProgress';
import type { OnboardingRequirement, OnboardingRun, OnboardingTemplate } from '~/types/models';

const COMPLETE_STATUSES = new Set<OnboardingRequirement['status']>(['completed', 'skipped']);
const PORTAL_CHECKLIST_TYPES = new Set<OnboardingRequirement['type']>([
    'portal_activation',
    'form',
    'contract',
    'quote',
    'proposal',
    'invoice',
    'payment',
    'file_upload',
]);

export function isOnboardingRequirementComplete(requirement: OnboardingRequirement): boolean {
    return COMPLETE_STATUSES.has(requirement.status);
}

export function isOnboardingClientActionRequirement(requirement: OnboardingRequirement): boolean {
    return ['form', 'file_upload'].includes(requirement.type);
}

export function getCurrentOnboardingStageId(run: Pick<OnboardingRun, 'currentStageId' | 'requirements' | 'stages'>): string {
    const stages = sortOnboardingStages(run.stages || []);
    const requirements = Array.isArray(run.requirements) ? run.requirements : [];
    const firstIncompleteStage = stages.find((stage) => (
        requirements.some((requirement) => requirement.stageId === stage.id && !isOnboardingRequirementComplete(requirement))
    ));

    return firstIncompleteStage?.id || run.currentStageId || stages[0]?.id || '';
}

export function getStageRequirements(requirements: OnboardingRequirement[], stageId?: string): OnboardingRequirement[] {
    if (!stageId) return [];
    return requirements.filter((requirement) => requirement.stageId === stageId);
}

export function getIncompleteOnboardingStageRequirements(
    run: Pick<OnboardingRun, 'currentStageId' | 'requirements' | 'stages'>,
    stageId = getCurrentOnboardingStageId(run),
): OnboardingRequirement[] {
    return getStageRequirements(Array.isArray(run.requirements) ? run.requirements : [], stageId)
        .filter((requirement) => !isOnboardingRequirementComplete(requirement));
}

export function getNextOnboardingStageId(run: Pick<OnboardingRun, 'currentStageId' | 'requirements' | 'stages'>): string {
    const stages = sortOnboardingStages(run.stages || []);
    const currentStageId = getCurrentOnboardingStageId(run);
    const currentIndex = stages.findIndex((stage) => stage.id === currentStageId);

    if (currentIndex < 0) {
        return stages[0]?.id || '';
    }

    return stages[currentIndex + 1]?.id || '';
}

export function getPortalChecklistRequirements(
    run: Pick<OnboardingRun, 'currentStageId' | 'requirements' | 'stages'>,
    stageId = getCurrentOnboardingStageId(run),
): OnboardingRequirement[] {
    return getStageRequirements(Array.isArray(run.requirements) ? run.requirements : [], stageId)
        .filter((requirement) => !isOnboardingRequirementComplete(requirement))
        .filter((requirement) => (
            PORTAL_CHECKLIST_TYPES.has(requirement.type)
            || (requirement.type === 'task' && Boolean(requirement.actionTaskId || requirement.actionPath))
        ));
}

/** Invitation timing follows requirement stages, never stage names or IDs. */
export function getOnboardingInvitationTiming(template: Pick<OnboardingTemplate, 'stages' | 'requirements'>) {
    const stages = sortOnboardingStages(template.stages || []);
    const requirements = template.requirements || [];
    const firstActionStage = stages.find(stage => requirements.some(requirement => requirement.stageId === stage.id));
    const portalStage = stages.find(stage => requirements.some(requirement => (
        requirement.type === 'portal_activation' && requirement.stageId === stage.id
    )));
    return {
        firstActionStage: firstActionStage || null,
        portalStage: portalStage || null,
        canInviteAtStart: Boolean(portalStage && firstActionStage?.id === portalStage.id),
    };
}
